import { Field } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import FormInput from "../../Form/FormInput";
import FormLabel from "../../Form/FormLabel";
import FormSelect from "../../Form/FormSelect";
import InputError from "../../Form/InputError";
import { TAX_BASE_TYPES } from "../../../../Constants/products";

/**
 * DPP (Nilai Lain) picker shared by Produk and Uang Muka: how the DPP is
 * worked out (decimal, percentage or fraction), its value, and the resulting
 * DPP in rupiah, read-only.
 *
 * `types` narrows the choices (a deposit only takes a fraction). `error` is
 * one message for the whole field, shown under the inputs.
 */
const TaxBaseField = ({
  id = "taxBaseType",
  label = "products.fields.taxBase",
  description,
  types = TAX_BASE_TYPES,
  type,
  onTypeChange,
  value,
  onValueChange,
  numerator,
  onNumeratorChange,
  denominator,
  onDenominatorChange,
  result,
  numberFormat = { thousandSeparator: true },
  errors = {},
  error,
}) => {
  const { t } = useTranslation();

  return (
    <Field>
      <FormLabel htmlFor={id}>
        <span>{t(label)}</span>
      </FormLabel>
      {description && (
        <p className="text-xs text-gray-500">{t(description)}</p>
      )}
      {/* mt-1 collapses into the label row's mb-1, so the select sits as far
          below the label (or its description) as every other field's. */}
      <div className="mt-1 space-y-2">
        <FormSelect
          id={id}
          name={id}
          options={types}
          value={type}
          onChange={onTypeChange}
          onClear={() => onTypeChange(null)}
          placeholder={t("products.placeholders.taxBaseType")}
          error={errors.taxBaseType}
        />

        {type?.value === "DECIMAL" && (
          <FormInput
            type="number"
            step="0.01"
            placeholder={t("products.placeholders.taxBaseDecimal")}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            error={errors.taxBaseValue}
          />
        )}

        {type?.value === "PERCENTAGE" && (
          <FormInput
            type="number"
            placeholder={t("products.placeholders.taxBasePercentage")}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            prefix="%"
            error={errors.taxBaseValue}
          />
        )}

        {type?.value === "FRACTION" && (
          <div className="flex items-center gap-2">
            <FormInput
              type="number"
              id={`${id}-numerator`}
              placeholder={t("products.placeholders.taxBaseNumerator")}
              value={numerator}
              onChange={(e) => onNumeratorChange(e.target.value)}
              error={errors.taxBaseNumerator}
            />
            <span>/</span>
            <FormInput
              type="number"
              id={`${id}-denominator`}
              placeholder={t("products.placeholders.taxBaseDenominator")}
              value={denominator}
              onChange={(e) => onDenominatorChange(e.target.value)}
              error={errors.taxBaseDenominator}
            />
          </div>
        )}

        {type?.value && (
          <FormInput
            id={`${id}-result`}
            disabled
            value={result}
            prefix="Rp"
            numberFormat={numberFormat}
          />
        )}

        {error && <InputError id={`${id}-fraction`} error={error} />}
      </div>
    </Field>
  );
};

export default TaxBaseField;
